import React, { useState, useMemo } from 'react';

import { Timestamp } from 'firebase/firestore';
import { Clock } from 'lucide-react';
import { jobsCol } from './database.js';

import { createJobDoc, updateOne } from './lib/db-crud.js';
import { saveTaxPreset } from './lib/use-business-profile.js';
import { toDate, normalizeLineItems, computeJobTotal, normalizeJobTax, computeJobTax, computeGrandTotal } from './lib/job-helpers.js';
import { vehicleLabel } from './Vehicles.jsx';

import JobLineItemsEditor from './JobLineItemsEditor.jsx';
import RecommendationsEditor from './RecommendationsEditor.jsx';

import { useInspection } from './lib/use-inspection.js';
import { inspectionFindings } from './lib/inspection-helpers.js';
import { normalizeRecommendations } from './lib/recommendations.js';

function pad2(n) { return String(n).padStart(2, '0'); }
function dateToInput(d) {
  if (!d) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function timeToInput(d) {
  if (!d) return '';
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function combineDateAndTime(dateStr, timeStr) {
  const [y, m, day] = dateStr.split('-').map(Number);
  const [h, min] = timeStr.split(':').map(Number);
  return new Date(y, m - 1, day, h, min, 0, 0);
}

function JobModal({ initial, customerId, vehicles, laborPresets = [], taxPresets = [], partSearchLinks, onClose }) {
  // Findings from this job's inspection seed the recommendations, so
  // the tech prices what they already wrote down instead of retyping it.
  const { inspection } = useInspection(initial.id || null);
  const findings = useMemo(() => inspectionFindings(inspection), [inspection]);
  const isEditing = !!initial.id;
  const [description, setDescription] = useState(initial.description || '');
  const [vehicleId, setVehicleId] = useState(initial.vehicleId || '');
  // Parts/labor split — normalize legacy single-line jobs to the new
  // shape on load so the editor doesn't have to handle both formats.
  const [lineItems, setLineItems] = useState(() => normalizeLineItems(initial.lineItems));
  const [shopSupplies, setShopSupplies] = useState(Number(initial.shopSupplies) || 0);
  // Sales tax. null = no tax on this job, which is every job that
  // existed before this shipped and the default for every new one.
  const [tax, setTax] = useState(() => normalizeJobTax(initial.tax));
  const [recommendations, setRecommendations] = useState(
    () => normalizeRecommendations(initial.recommendations)
  );
  // Schedule fields — optional. Empty values = job stays unscheduled.
  // Editing a previously-scheduled job pre-fills these from initial.
  const initialScheduledDate = toDate(initial.scheduledAt);
  const [scheduledDate, setScheduledDate] = useState(
    initialScheduledDate ? dateToInput(initialScheduledDate) : ''
  );
  const [scheduledTime, setScheduledTime] = useState(
    initialScheduledDate ? timeToInput(initialScheduledDate) : ''
  );
  const [duration, setDuration] = useState(
    initial.durationMinutes ? String(initial.durationMinutes) : ''
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const selectedVehicleLabel = vehicleLabel(vehicles.find((v) => v.id === vehicleId));

  async function handleSubmit(e) {
    e.preventDefault();
    const desc = description.trim();
    if (!vehicleId) { setError('Pick a vehicle.'); return; }
    if (!desc) { setError('Description is required.'); return; }
    const total = computeJobTotal({ lineItems, shopSupplies });
    if (!isFinite(total) || total < 0) { setError('Total came out invalid — check the line items.'); return; }

    // Schedule fields are optional, but partial = error. Either set
    // both date AND time (to schedule) or leave both blank (unscheduled).
    let scheduledAtValue = null;
    let durationValue    = null;
    const hasDate = !!scheduledDate;
    const hasTime = !!scheduledTime;
    if (hasDate !== hasTime) {
      setError('Set BOTH a date and a start time, or clear both to leave this job unscheduled.');
      return;
    }
    if (hasDate && hasTime) {
      try {
        const d = combineDateAndTime(scheduledDate, scheduledTime);
        if (isNaN(d.getTime())) throw new Error('bad date');
        scheduledAtValue = Timestamp.fromDate(d);
      } catch (_) {
        setError('That date/time looks off — check the values.');
        return;
      }
      const dur = Number(duration) || 60;
      if (!isFinite(dur) || dur < 15) {
        setError('Duration must be at least 15 minutes (or leave it blank for the 60-min default).');
        return;
      }
      durationValue = dur;
    }

    setSubmitting(true);
    setError(null);
    try {
      const cleanTax = normalizeJobTax(tax);
      const payload = {
        description: desc,
        vehicleId,
        lineItems,
        shopSupplies: Number(shopSupplies) || 0,
        totalPrice: total, // auto-computed, PRE-TAX, kept for backward compat
        // Tax lives beside the subtotal, never inside it: totalPrice
        // feeds Earnings and the CSV export, and sales tax collected
        // is not income. null when untaxed so the field simply isn't
        // there, exactly as on every job written before today.
        tax: cleanTax,
        taxTotal: computeJobTax({ lineItems, shopSupplies, tax: cleanTax }).total,
        grandTotal: computeGrandTotal({ lineItems, shopSupplies, tax: cleanTax }),
        scheduledAt:     scheduledAtValue, // null clears any prior schedule
        durationMinutes: durationValue,
        recommendations: normalizeRecommendations(recommendations),
      };
      if (isEditing) {
        await updateOne(jobsCol(), initial.id, payload);
      } else {
        await createJobDoc(jobsCol(), {
          ...payload,
          customerId,
          status: 'draft',
          shareId: null,
          invoiceShareId: null,
          sentAt: null,
          approvedAt: null,
          completedAt: null,
          paidAt: null,
        });
      }
      onClose();
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box modal-box-wide" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Job' : 'Add Job'}</h2>
          <button type="button" className="btn-link" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="form">
          <label className="form-row">
            <span>Vehicle</span>
            <select required value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
              <option value="">— Select a vehicle —</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>{vehicleLabel(v)}</option>
              ))}
            </select>
          </label>

          {/* Schedule — optional. Both date AND time blank = unscheduled.
              Filling both = scheduled; duration defaults to 60 if blank. */}
          <div className="form-row-pair">
            <label className="form-row" style={{ flex: 1 }}>
              <span>
                <Clock size={11} style={{ marginRight: 4, verticalAlign: '-1px' }} />
                Date (optional)
              </span>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
              />
            </label>
            <label className="form-row" style={{ flex: 1 }}>
              <span>Start time</span>
              <input
                type="time" step="900"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
              />
            </label>
            <label className="form-row" style={{ flex: '0 0 110px' }}>
              <span>Duration (min)</span>
              <input
                type="number" min={15} step={15} inputMode="numeric"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="60"
              />
            </label>
          </div>

          <label className="form-row">
            <span>Description</span>
            <textarea
              required maxLength={500} rows={2} autoFocus
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Replace battery + alternator"
            />
          </label>
          <JobLineItemsEditor
            lineItems={lineItems}
            shopSupplies={shopSupplies}
            laborPresets={laborPresets}
            taxPresets={taxPresets}
            tax={tax}
            partSearchLinks={partSearchLinks}
            vehicleLabel={selectedVehicleLabel}
            onLineItemsChange={setLineItems}
            onShopSuppliesChange={setShopSupplies}
            onTaxChange={setTax}
            onSaveTaxPreset={saveTaxPreset}
          />

          {/* Only worth showing once there's an inspection to draw
              from or recommendations already on the job — a brand-new
              job doesn't need a third empty section. */}
          {(findings.length > 0 || recommendations.length > 0) && (
            <RecommendationsEditor
              recommendations={recommendations}
              findings={findings}
              onChange={setRecommendations}
            />
          )}
          {error && <p className="form-error">{error}</p>}
          <div className="form-actions">
            <button type="button" className="btn-link" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn" disabled={submitting}>
              {submitting ? 'Saving…' : isEditing ? 'Save changes' : 'Add Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export { JobModal };
